import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';

export interface SessionMonitorProps {
  /**
   * Position on screen
   * @default 'top-right'
   */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

  /**
   * Warning threshold in seconds (yellow state)
   * @default 300 (5 minutes)
   */
  warningThreshold?: number;

  /**
   * Critical threshold in seconds (red state)
   * @default 60 (1 minute)
   */
  criticalThreshold?: number;

  /**
   * Show progress bar
   * @default true
   */
  showProgressBar?: boolean;

  /**
   * Allow minimize/expand
   * @default true
   */
  minimizable?: boolean;

  /**
   * Start minimized
   * @default false
   */
  startMinimized?: boolean;

  /**
   * Custom CSS class names
   */
  className?: string;

  /**
   * Callback when warning threshold is reached
   */
  onWarning?: (secondsRemaining: number) => void;

  /**
   * Callback when critical threshold is reached
   */
  onCritical?: (secondsRemaining: number) => void;

  /**
   * Callback when session expires
   */
  onExpired?: () => void;

  /**
   * Custom inline styles
   */
  style?: React.CSSProperties;
}

/**
 * Session monitoring component with countdown timer
 *
 * Displays the time remaining until the user's session expires. Shows visual
 * warnings when the session is about to expire and triggers callbacks for
 * custom handling.
 *
 * @example Basic usage
 * ```tsx
 * import { SessionMonitor } from 'antaeus.keycloak.react';
 *
 * function App() {
 *   return (
 *     <>
 *       <SessionMonitor />
 *       <YourApp />
 *     </>
 *   );
 * }
 * ```
 *
 * @example With custom thresholds
 * ```tsx
 * <SessionMonitor
 *   warningThreshold={600}  // 10 minutes
 *   criticalThreshold={120} // 2 minutes
 *   onWarning={(seconds) => {
 *     console.log(`Session expiring in ${seconds} seconds`);
 *   }}
 * />
 * ```
 *
 * @example Custom position and style
 * ```tsx
 * <SessionMonitor
 *   position="bottom-left"
 *   showProgressBar={false}
 *   minimizable={false}
 * />
 * ```
 */
export const SessionMonitor: React.FC<SessionMonitorProps> = ({
  position = 'top-right',
  warningThreshold = 300, // 5 minutes
  criticalThreshold = 60, // 1 minute
  showProgressBar = true,
  minimizable = true,
  startMinimized = false,
  className = '',
  onWarning,
  onCritical,
  onExpired,
  style,
}) => {
  const auth = useAuth();
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [initialTimeRemaining, setInitialTimeRemaining] = useState<number | null>(null);
  const [minimized, setMinimized] = useState(startMinimized);
  const [warningTriggered, setWarningTriggered] = useState(false);
  const [criticalTriggered, setCriticalTriggered] = useState(false);

  // Update time remaining every second
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.expires_at) {
      setTimeRemaining(null);
      setInitialTimeRemaining(null);
      return;
    }

    let logoutTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const updateTimeRemaining = () => {
      const expiresAt = auth.user!.expires_at!;
      const now = Math.floor(Date.now() / 1000);
      const remaining = expiresAt - now;

      // Store initial time remaining on first calculation
      if (initialTimeRemaining === null && remaining > 0) {
        setInitialTimeRemaining(remaining);
      }

      if (remaining <= 0) {
        setTimeRemaining(0);

        // Trigger expired callback
        onExpired?.();

        // Auto logout after 2 seconds (with cleanup)
        logoutTimeoutId = setTimeout(() => {
          auth.signoutRedirect();
        }, 2000);
      } else {
        setTimeRemaining(remaining);

        // Trigger callbacks once
        if (remaining <= criticalThreshold && !criticalTriggered) {
          setCriticalTriggered(true);
          onCritical?.(remaining);
        } else if (remaining <= warningThreshold && !warningTriggered) {
          setWarningTriggered(true);
          onWarning?.(remaining);
        }
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => {
      clearInterval(interval);
      if (logoutTimeoutId) {
        clearTimeout(logoutTimeoutId);
      }
    };
  }, [
    auth,
    warningThreshold,
    criticalThreshold,
    warningTriggered,
    criticalTriggered,
    initialTimeRemaining,
    onWarning,
    onCritical,
    onExpired,
  ]);

  // Reset warning flags when thresholds change
  useEffect(() => {
    if (timeRemaining && timeRemaining > warningThreshold) {
      setWarningTriggered(false);
      setCriticalTriggered(false);
    }
  }, [timeRemaining, warningThreshold]);

  // Format time as MM:SS
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Get status class based on time remaining
  const getStatusClass = useCallback((): string => {
    if (!timeRemaining) return 'normal';
    if (timeRemaining <= criticalThreshold) return 'critical';
    if (timeRemaining <= warningThreshold) return 'warning';
    return 'normal';
  }, [timeRemaining, warningThreshold, criticalThreshold]);

  // Calculate progress percentage
  const getProgress = useCallback((): number => {
    if (!timeRemaining || !initialTimeRemaining) return 100;
    return Math.max(0, Math.min(100, (timeRemaining / initialTimeRemaining) * 100));
  }, [timeRemaining, initialTimeRemaining]);

  // Don't render if not authenticated or no time info
  if (!auth.isAuthenticated || timeRemaining === null) {
    return null;
  }

  const baseClass = 'kc-session-monitor';
  const positionClass = `kc-session-monitor--${position}`;
  const statusClass = `kc-session-monitor--${getStatusClass()}`;
  const minimizedClass = minimized ? 'kc-session-monitor--minimized' : '';
  const classes = `${baseClass} ${positionClass} ${statusClass} ${minimizedClass} ${className}`.trim();

  const progress = getProgress();

  return (
    <div className={classes} style={style}>
      {minimizable && (
        <button
          className="kc-session-monitor__toggle"
          onClick={() => setMinimized(!minimized)}
          aria-label={minimized ? 'Expand session monitor' : 'Minimize session monitor'}
          title={minimized ? 'Expand' : 'Minimize'}
        >
          {minimized ? '▲' : '▼'}
        </button>
      )}

      {!minimized && (
        <>
          <div className="kc-session-monitor__content">
            <span className="kc-session-monitor__label">
              {timeRemaining === 0 ? 'Session expired' : 'Session expires in:'}
            </span>
            <span className="kc-session-monitor__time">
              {formatTime(timeRemaining)}
            </span>
          </div>

          {showProgressBar && (
            <div className="kc-session-monitor__progress-bar">
              <div
                className="kc-session-monitor__progress-fill"
                style={{ width: `${progress}%` }}
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
